<template src='./new.pug' lang='pug'></template>

<script>
import { Event } from 'vue-tables-2'
import PageTitle from '../../../layout/pageTitle/PageTitle'
import FilterPortlet from '../../../layout/filterPortlet/FilterPortlet'
import StepProgress from '../../../step/StepProgress'
import ButtonPrimary from '../../../buttons/buttonPrimary/ButtonPrimary'
import { trackAnalyticsEvent } from '../../../../utils/analytics.js'
import customSorter from '../../../../utils/customSorter.js'
import { filterByBank, filterByAgency, filterByAccount } from '../../../../utils/customFilters.js'
import looseDeepEqual from '../../../../utils/looseDeepEqual'
import PerfilAcessoEnum from '../../../../enum/perfilAcessoEnum'
import PATHS from '../../../../router/routesMap.js'

export default {
  name: 'AccessProfileNew',

  components: {
    StepProgress,
    PageTitle,
    FilterPortlet,
    ButtonPrimary
  },

  data () {
    return {
      steps: [
        this.$t('administracao.perfilAcesso.etapas.informacoesPerfil'),
        this.$t('administracao.perfilAcesso.etapas.estabelecimentos'),
        this.$t('administracao.perfilAcesso.etapas.contasBancarias'),
        this.$t('administracao.perfilAcesso.etapas.confirmacao')
      ],
      currentStep: 0,
      profile: {
        name: '',
        visibility: '',
        functionalitiesSelected: [],
        establishmentsSelected: [],
        bankAccountsSelected: []
      },
      functionalitiesGroups: [],
      totalFunctionalities: null,
      allFunctionalitiesSelected: false,
      establishments: {
        establishments: [],
        allEstablishmentsSelected: false,
        columns: ['check', 'nome', 'nomeFantasia', 'cnpj'],
        options: {
          headings: {
            nome: this.$t('estabelecimentos.labels.nome'),
            nomeFantasia: this.$t('estabelecimentos.labels.nomeFantasia'),
            cnpj: this.$t('estabelecimentos.labels.cnpj')
          },
          columnsClasses: {
            nome: 'col-md-4',
            nomeFantasia: 'col-md-4',
            cnpj: 'col-md-4'
          },
          filterable: ['nome', 'nomeFantasia', 'cnpj'],
          filterByColumn: true,
          perPageValues: [],
          sortable: ['nome', 'nomeFantasia', 'cnpj'],
          sortIcon: { is: '' },
          pagination: { chunk: 5 },
          perPage: Number.MAX_SAFE_INTEGER,
          texts: {
            count: '',
            noResults: this.$t('dataTable.labels.semResultados'),
            filterBy: '',
            filterPlaceholder: this.$t('dataTable.labels.filtrar')
          }
        }
      },
      bankAccounts: {
        accounts: [],
        allBankAccountsSelected: false,
        columns: ['check', 'nome', 'banco', 'agencia', 'conta'],
        options: {
          headings: {
            nome: this.$t('contaBancaria.labels.nome'),
            banco: this.$t('contaBancaria.labels.banco'),
            agencia: this.$t('contaBancaria.labels.agencia'),
            conta: this.$t('contaBancaria.labels.numeroConta')
          },
          columnsClasses: {
            nome: 'col-md-3',
            banco: 'col-md-4',
            agencia: 'col-md-2',
            conta: 'col-md-3'
          },
          filterable: ['nome'],
          customFilters: [{
            name: 'banco',
            callback: filterByBank
          }, {
            name: 'agencia',
            callback: filterByAgency
          }, {
            name: 'conta',
            callback: filterByAccount
          }],
          filterByColumn: true,
          perPageValues: [],
          sortable: ['nome', 'banco', 'agencia', 'conta'],
          sortIcon: { is: '' },
          customSorting: {
            nome: function (ascending) {
              return function (a, b) {
                return customSorter.stringSorter(ascending, a, b, 'nome')
              }
            },
            banco: (ascending) => {
              return (a, b) => {
                let lastA = a.banco.codigo
                let lastB = b.banco.codigo
                if (ascending) {
                  return lastA <= lastB ? 1 : -1
                }
                return lastA >= lastB ? 1 : -1
              }
            },
            agencia: (ascending) => {
              return (a, b) => {
                let lastA = a.agencia
                lastA.agencia = a.numAgencia + (a.digAgencia ? '-' + a.digAgencia : '')

                let lastB = b.agencia
                lastB.agencia = b.numAgencia + (b.digAgencia ? '-' + b.digAgencia : '')

                return customSorter.stringSorter(ascending, lastA, lastB, 'agencia')
              }
            },
            conta: function (ascending) {
              return function (a, b) {
                return customSorter.stringSorter(ascending, a, b, 'conta')
              }
            }
          },
          pagination: { chunk: 5 },
          perPage: Number.MAX_SAFE_INTEGER,
          templates: {
            nome: (h, row) => row.nome + (row.ativo ? '' : ' (' + this.$t('contaBancaria.labels.inativa') + ')'),
            banco: (h, row) => row.banco.codigo + '-' + row.banco.nomeFantasia,
            agencia: (h, row) => row.numAgencia + (row.digAgencia ? '-' + row.digAgencia : ''),
            conta: (h, row) => row.conta + (row.digConta ? '-' + row.digConta : '')
          },
          texts: {
            count: '',
            noResults: this.$t('dataTable.labels.semResultados'),
            filterBy: '',
            filterPlaceholder: this.$t('dataTable.labels.filtrar')
          }
        }
      },
      requestSaveProfileSent: false
    }
  },

  computed: {
    profileToEditId () {
      return this.$store.getters.profileToEditId
    },

    pageTitleSubTitle () {
      return this.profileToEditId ? this.$t('administracao.perfilAcesso.titulos.edicaoSubtitulo')
        : this.$t('administracao.perfilAcesso.titulos.cadastroSubtitulo')
    },

    filterPortletTitle () {
      return this.profileToEditId ? this.$t('administracao.perfilAcesso.titulos.editarPerfil')
        : this.$t('administracao.perfilAcesso.titulos.novoPerfil')
    }
  },

  watch: {
    'profile.name': {
      handler () {
        const objectToCompare = {
          name: this.profile.name,
          visibility: this.profile.visibility,
          functionalities: this.profile.functionalitiesSelected.map(fun => fun.id).sort((a, b) => a - b),
          establishments: this.profile.establishmentsSelected.map(estab => estab.id).sort((a, b) => a - b),
          bankAccounts: this.profile.bankAccountsSelected.map(account => account.id).sort((a, b) => a - b)
        }
        this.$store.dispatch('setHasUnsavedChanges', !looseDeepEqual(this.defaultObject, objectToCompare))
      },
      deep: true
    },
    'profile.visibility': {
      handler () {
        const objectToCompare = {
          name: this.profile.name,
          visibility: this.profile.visibility,
          functionalities: this.profile.functionalitiesSelected.map(fun => fun.id).sort((a, b) => a - b),
          establishments: this.profile.establishmentsSelected.map(estab => estab.id).sort((a, b) => a - b),
          bankAccounts: this.profile.bankAccountsSelected.map(account => account.id).sort((a, b) => a - b)
        }
        this.$store.dispatch('setHasUnsavedChanges', !looseDeepEqual(this.defaultObject, objectToCompare))
      },
      deep: true
    },
    'profile.functionalitiesSelected': {
      handler () {
        const objectToCompare = {
          name: this.profile.name,
          visibility: this.profile.visibility,
          functionalities: this.profile.functionalitiesSelected.map(fun => fun.id).sort((a, b) => a - b),
          establishments: this.profile.establishmentsSelected.map(estab => estab.id).sort((a, b) => a - b),
          bankAccounts: this.profile.bankAccountsSelected.map(account => account.id).sort((a, b) => a - b)
        }
        this.$store.dispatch('setHasUnsavedChanges', !looseDeepEqual(this.defaultObject, objectToCompare))
      },
      deep: true
    },
    'profile.establishmentsSelected': {
      handler () {
        const objectToCompare = {
          name: this.profile.name,
          visibility: this.profile.visibility,
          functionalities: this.profile.functionalitiesSelected.map(fun => fun.id).sort((a, b) => a - b),
          establishments: this.profile.establishmentsSelected.map(estab => estab.id).sort((a, b) => a - b),
          bankAccounts: this.profile.bankAccountsSelected.map(account => account.id).sort((a, b) => a - b)
        }
        this.$store.dispatch('setHasUnsavedChanges', !looseDeepEqual(this.defaultObject, objectToCompare))
      },
      deep: true
    },
    'profile.bankAccountsSelected': {
      handler () {
        const objectToCompare = {
          name: this.profile.name,
          visibility: this.profile.visibility,
          functionalities: this.profile.functionalitiesSelected.map(fun => fun.id).sort((a, b) => a - b),
          establishments: this.profile.establishmentsSelected.map(estab => estab.id).sort((a, b) => a - b),
          bankAccounts: this.profile.bankAccountsSelected.map(account => account.id).sort((a, b) => a - b)
        }
        this.$store.dispatch('setHasUnsavedChanges', !looseDeepEqual(this.defaultObject, objectToCompare))
      },
      deep: true
    }
  },

  methods: {
    /** CARREGAR DADOS INICIAIS */
    async loadData () {
      try {
        await this.loadEstablishments()
        await this.loadBankAccounts()
        await this.loadFunctionalitiesGroups()
      } catch (error) {
        this.$root.$refs.notification.error(error.message)
      }
    },

    /** PERFIL DE ACESSO **/
    loadProfile () {
      this.$store.dispatch('getProfile', this.profileToEditId)
        .then((response) => {
          const profileToEdit = response.data

          this.profile.name = profileToEdit.nome
          profileToEdit.grupoFuncionalidades.forEach(functionalityGroupSelected => {
            functionalityGroupSelected.funcionalidades.forEach(functionalitySelected => {
              this.functionalitiesGroups.forEach(functionalityGroup => {
                functionalityGroup.funcionalidades.forEach(functionality => {
                  if (functionality.id === functionalitySelected.id) {
                    functionality.checked = true
                    this.selectFunctionality(functionality)
                    return
                  }
                })
              })
            })
          })
          if (profileToEdit.perfilAcessoBanking) {
            /*
              No BD a visibilidade para estabelecimento e conta bancarias é salva em campos separados,
              porem na view é utilizado apenas um campo para representar os dois campos
            */
            this.profile.visibility = profileToEdit.perfilAcessoBanking.visibilidadeEstabelecimento

            profileToEdit.perfilAcessoBanking.perfilAcessoEstabelecimentos.forEach(establishmentSelected => {
              this.establishments.establishments.forEach(establishment => {
                if (establishmentSelected.estabelecimento.id === establishment.id) {
                  this.selectEstablishment(establishment)
                  return
                }
              })
            })

            profileToEdit.perfilAcessoBanking.perfilAcessoContas.forEach(bankAccountSelected => {
              this.bankAccounts.accounts.forEach(bankAccount => {
                if (bankAccountSelected.conta.id === bankAccount.id) {
                  this.selectBankAccount(bankAccount)
                  return
                }
              })
            })
          } else {
            this.profile.visibility = '0'
          }

          this.defaultObject = {
            name: this.profile.name,
            visibility: this.profile.visibility,
            functionalities: this.profile.functionalitiesSelected.map(fun => fun.id).sort((a, b) => a - b),
            establishments: this.profile.establishmentsSelected.map(estab => estab.id).sort((a, b) => a - b),
            bankAccounts: this.profile.bankAccountsSelected.map(account => account.id).sort((a, b) => a - b)
          }
        })
    },

    isMasterProfile (accessProfileId) {
      return Number(accessProfileId) === PerfilAcessoEnum.MASTER
    },

    /** FUNCIONALIDADES **/
    loadFunctionalitiesGroups () {
      let self = this
      return new Promise((resolve, reject) => {
        this.$store.dispatch('getFunctionalitiesGroups', '/accessProfile/functionalitiesGroups')
        .then((response) => {
          this.totalFunctionalities = 0
          this.functionalitiesGroups = response.data
          let remove = []
          this.functionalitiesGroups.forEach((functionalityGroup, index, list) => {
            if (functionalityGroup.funcionalidades && functionalityGroup.funcionalidades.length > 0) {
              this.$set(functionalityGroup, 'checked', false)
              this.$set(functionalityGroup, 'selectedFuncs', [])
              if (!self.$store.getters.isClientEmulatedLogin) {
                if (functionalityGroup.nome === self.$t('menu.grupos.registro')) {
                  functionalityGroup.funcionalidades = functionalityGroup.funcionalidades.filter(funcionalidade => {
                    return funcionalidade.nome !== self.$t('menu.funcionalidades.categorias') &&
                    funcionalidade.nome !== self.$t('menu.funcionalidades.planosConciliacao') &&
                    funcionalidade.nome !== self.$t('conjuntoAtributo.titulos.atributos')
                  })
                }
              }
              functionalityGroup.funcionalidades.forEach(functionality => {
                this.$set(functionality, 'checked', false)
                this.totalFunctionalities++
              })
            } else {
              remove.push(functionalityGroup)
            }
          })

          remove.forEach(group => {
            let idx = this.functionalitiesGroups.indexOf(group)
            if (idx >= 0) {
              this.functionalitiesGroups.splice(idx, 1)
            }
          })

          if (this.profileToEditId) {
            this.loadProfile()
          } else {
            this.defaultObject = {
              name: this.profile.name,
              visibility: this.profile.visibility,
              functionalities: this.profile.functionalitiesSelected.map(fun => fun.id).sort((a, b) => a - b),
              establishments: this.profile.establishmentsSelected.map(estab => estab.id).sort((a, b) => a - b),
              bankAccounts: this.profile.bankAccountsSelected.map(account => account.id).sort((a, b) => a - b)
            }
          }
          resolve()
        }).catch((error) => {
          reject(error)
        })
      })
    },

    trackEventOnFunctionality (functionalityName) {
      trackAnalyticsEvent(this, 'checkbox-click', 'select-funcionalidade', functionalityName)
    },

    trackEventOnFunctionalityGroup (functionalityGroupName) {
      trackAnalyticsEvent(this, 'checkbox-click', 'select-grupo-de-funcionalidade', functionalityGroupName)
    },

    selectAllFunctionalities () {
      this.allFunctionalitiesSelected = !this.allFunctionalitiesSelected
      this.profile.functionalitiesSelected = []

      this.functionalitiesGroups.forEach(gf => {
        gf.checked = this.allFunctionalitiesSelected
        gf.selectedFuncs = this.allFunctionalitiesSelected ? gf.funcionalidades.slice() : []
        gf.funcionalidades.forEach(f => {
          f.checked = this.allFunctionalitiesSelected
          this.allFunctionalitiesSelected ? this.pushItemToArray(f, this.profile.functionalitiesSelected) : this.spliceItemToArray(f, this.profile.functionalitiesSelected)
        })
      })
    },

    selectFunctionalityGroup (functionalityGroup) {
      let groupIndex = this.functionalitiesGroups.indexOf(functionalityGroup)
      this.functionalitiesGroups[groupIndex].funcionalidades.forEach(f => {
        f.checked = this.functionalitiesGroups[groupIndex].checked
        if (this.functionalitiesGroups[groupIndex].checked) {
          this.pushItemToArray(f, this.profile.functionalitiesSelected)
          this.pushItemToArray(f, this.functionalitiesGroups[groupIndex].selectedFuncs)
        } else {
          this.spliceItemToArray(f, this.profile.functionalitiesSelected)
          this.spliceItemToArray(f, this.functionalitiesGroups[groupIndex].selectedFuncs)
        }
      })

      this.allFunctionalitiesSelected = (this.profile.functionalitiesSelected.length === this.totalFunctionalities)
      this.functionalitiesGroups[groupIndex].checked = (this.functionalitiesGroups[groupIndex].selectedFuncs.length >= this.functionalitiesGroups[groupIndex].funcionalidades.length)
    },

    selectFunctionality (functionality) {
      let gindex = this.getGroupIndexById(functionality.grupoFuncionalidade.id)

      if (functionality.checked) {
        this.pushItemToArray(functionality, this.profile.functionalitiesSelected)
        if (this.profile.functionalitiesSelected.length === this.totalFunctionalities) {
          this.allFunctionalitiesSelected = true
        } else {
          this.allFunctionalitiesSelected = false
        }
        this.pushItemToArray(functionality, this.functionalitiesGroups[gindex].selectedFuncs)
        if (this.functionalitiesGroups[gindex].selectedFuncs.length === this.functionalitiesGroups[gindex].funcionalidades.length) {
          this.functionalitiesGroups[gindex].checked = true
        } else {
          this.functionalitiesGroups[gindex].checked = false
        }
      } else {
        if (this.spliceItemToArray(functionality, this.profile.functionalitiesSelected)) {
          this.allFunctionalitiesSelected = false
        }

        if (this.spliceItemToArray(functionality, this.functionalitiesGroups[gindex].selectedFuncs)) {
          this.functionalitiesGroups[gindex].checked = false
        }
      }
    },

    getGroupIndexById (groupId) {
      let gIndex = -1
      this.functionalitiesGroups.forEach((group, index) => {
        if (group.id === groupId) {
          gIndex = index
          return gIndex
        }
      })
      return gIndex
    },

    pushItemToArray (item, array) {
      let idx = array.indexOf(item)
      if (idx === -1) {
        array.push(item)
      }
    },

    spliceItemToArray (item, array) {
      let idx = array.indexOf(item)
      if (idx > -1) {
        array.splice(idx, 1)
        return true
      }
      return false
    },

    getGroupIcon (group) {
      let icon = ''
      if (group === 31) {
        icon = 'fa fa-wrench'
      } else if (group === 32) {
        icon = 'fa fa-pencil-square-o'
      } else if (group === 33) {
        icon = 'fa fa-handshake-o'
      } else if (group === 34) {
        icon = 'fa fa-share-alt'
      } else if (group === 35) {
        icon = 'fa fa-window-restore'
      } else if (group === 36) {
        icon = 'fa fa-bar-chart'
      } else if (group === 37) {
        icon = 'fa fa-usd'
      }
      return icon
    },

    getCheckFuncContainerClass (index) {
      if (index % 2 === 0) {
        return 'col-xs-6 pull-left'
      } else {
        return 'col-xs-6 pull-right'
      }
    },

    /** ESTABELECIMENTOS **/
    loadEstablishments () {
      return new Promise((resolve, reject) => {
        this.$store.dispatch('getEstablishmentsToTreeGrid', {
          url: 'establishment/listDetails'
        })
        .then((response) => {
          const establishments = response.data.Body[0]
          this.establishments.establishments = establishments.map(establishment => {
            return {
              id: establishment.id,
              nome: establishment.Nome,
              nomeFantasia: establishment.NomeFantasia,
              cnpj: establishment.CNPJ ? establishment.CNPJ.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})/, '$1.$2.$3/$4-$5') : '-'
            }
          })
          resolve()
        }).catch((error) => {
          reject(error)
        })
      })
    },

    selectAllEstablishments () {
      this.establishments.allEstablishmentsSelected = !this.establishments.allEstablishmentsSelected
      this.establishments.establishments = this.establishments.establishments.map(establishment => {
        establishment.checked = this.establishments.allEstablishmentsSelected
        const index = this.profile.establishmentsSelected.findIndex(establishmentSelected => establishmentSelected['id'] === establishment['id'])
        if (index > -1 && !this.establishments.allEstablishmentsSelected) {
          this.profile.establishmentsSelected.splice(index, 1)
        } else if (index === -1 && this.establishments.allEstablishmentsSelected) {
          this.profile.establishmentsSelected.push(establishment)
        }
        return establishment
      })
    },

    selectEstablishment (establishmentClicked) {
      const establishmentSelected = !establishmentClicked.checked

      this.establishments.establishments = this.establishments.establishments.map(establishment => {
        if (establishment['id'] === establishmentClicked['id']) {
          establishment.checked = establishmentSelected
        }
        return establishment
      })

      if (establishmentSelected) {
        this.profile.establishmentsSelected.push(establishmentClicked)
      } else {
        const index = this.profile.establishmentsSelected.findIndex(
          establishment => establishment['id'] === establishmentClicked['id']
        )
        if (index > -1) {
          this.profile.establishmentsSelected.splice(index, 1)
        }
      }

      if (this.profile.establishmentsSelected.length === 0 || this.profile.establishmentsSelected.length !== this.establishments.establishments.length) {
        this.establishments.allEstablishmentsSelected = false
      } else if (this.profile.establishmentsSelected.length === this.establishments.establishments.length) {
        this.establishments.allEstablishmentsSelected = true
      }
    },

    /** CONTAS BANCARIAS **/
    loadBankAccounts () {
      return new Promise((resolve, reject) => {
        this.$store.dispatch('getBankAccounts', '/bankAccounts')
        .then((response) => {
          this.bankAccounts.accounts = response.data.data
          resolve()
        })
        .catch((error) => {
          reject(error)
        })
      })
    },

    handleFilter (event, column) {
      Event.$emit(`vue-tables.filter::${column}`, event.currentTarget.value)
    },

    selectAllBankAccounts () {
      this.bankAccounts.allBankAccountsSelected = !this.bankAccounts.allBankAccountsSelected
      this.bankAccounts.accounts = this.bankAccounts.accounts.map(account => {
        account.checked = this.bankAccounts.allBankAccountsSelected
        const index = this.profile.bankAccountsSelected.findIndex(accountSelected => accountSelected['id'] === account['id'])
        if (index > -1 && !this.bankAccounts.allBankAccountsSelected) {
          this.profile.bankAccountsSelected.splice(index, 1)
        } else if (index === -1 && this.bankAccounts.allBankAccountsSelected) {
          this.profile.bankAccountsSelected.push(account)
        }
        return account
      })
    },

    selectBankAccount (accountClicked) {
      const accountSelected = !accountClicked.checked

      this.bankAccounts.accounts = this.bankAccounts.accounts.map(account => {
        if (account['id'] === accountClicked['id']) {
          account.checked = accountSelected
        }
        return account
      })

      if (accountSelected) {
        this.profile.bankAccountsSelected.push(accountClicked)
      } else {
        const index = this.profile.bankAccountsSelected.findIndex(
          account => account['id'] === accountClicked['id']
        )
        if (index > -1) {
          this.profile.bankAccountsSelected.splice(index, 1)
        }
      }

      if (this.profile.bankAccountsSelected.length === 0 || this.profile.bankAccountsSelected.length !== this.bankAccounts.accounts.length) {
        this.bankAccounts.allBankAccountsSelected = false
      } else if (this.profile.bankAccountsSelected.length === this.bankAccounts.accounts.length) {
        this.bankAccounts.allBankAccountsSelected = true
      }
    },

    /** CADASTRO | ATUALIZACAO DO PERFIL DE ACESSO **/
    newProfile () {
      const profile = {
        id: this.profileToEditId,
        nome: this.profile.name.trim(),
        visibilidade: this.profile.visibility,
        funcionalidades: this.profile.functionalitiesSelected,
        estabelecimentos: this.profile.establishmentsSelected,
        contas: this.profile.bankAccountsSelected
      }

      this.requestSaveProfileSent = true
      this.$store.dispatch('newProfile', profile)
        .then((response) => {
          if (this.profileToEditId) {
            this.$root.$refs.notification.success('administracao.perfilAcesso.sucesso.editado')
          } else {
            this.$root.$refs.notification.success('administracao.perfilAcesso.sucesso.criado')
          }
          this.profiles = response.data
          this.$store.dispatch('setHasUnsavedChanges', false)
          this.$router.push(PATHS.LIST_ACCESS_PROFILE)
        }).catch((error) => {
          this.$root.$refs.notification.error(error.message)
        })
        .finally(() => {
          this.requestSaveProfileSent = false
        })
    },

    /** BOTOES **/
    nextStep () {
      if (this.currentStep < 3) {
        this.$validator.validateAll()
          .then((result) => {
            if (result) {
              if (this.profile.visibility === '1') {
                this.currentStep = this.currentStep + 1
              } else {
                this.currentStep = 3
              }
            } else {
              this.$root.$refs.notification.error('administracao.perfilAcesso.validacao.camposObrigatorios', 'administracao.perfilAcesso.erro.criar')
            }
          })
      }
    },

    backStep () {
      if (this.currentStep > 0) {
        if (this.profile.visibility === '1') {
          this.currentStep = this.currentStep - 1
        } else {
          this.currentStep = 0
        }
      }
    },

    back () {
      this.$router.push(PATHS.LIST_ACCESS_PROFILE)
    }
  },

  created () {
    this.loadData()
  },

  beforeDestroy () {
    this.$store.dispatch('setHasUnsavedChanges', false)
    this.$store.dispatch('cleanProfileToEdit')
  }
}
</script>

<style scoped>
.fa {
  font-size: 20px;
}

.check-group {
  margin-left: 0.7vw !important;
}

.checkbox {
  margin-top: 0px;
  margin-bottom: 0px;
  padding-left: 10px;
}

.group-checkbox {
  margin-top: 0px;
  margin-bottom: 0px;
  padding-left: 15px;
}

.boxed-container {
	border: 1px solid #ddd;
	background: #fff;
	color:#444;
  padding: 10px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
}

.func-group-title {
  color:#17375f;
  font-size: 14px !important;
  text-transform: none !important;
}

.func-group-title-label {
  margin-left: 0.5vw;
}

.func-group-labels {
  color:#515151;
  font-size: 13px !important;
  text-transform: initial !important;
  font-weight: normal !important;
  padding-top: 1px;
}

.bottom-bordered {
  border-bottom: 1px solid #17375f;
  margin-top: 0.4vh;
  margin-bottom: 0.7vh;
}

.func-padded {
  padding-left: 0.7vw;
}

.info-icon {
  margin-left: 15px;
  font-size: 15px;
  color: gray;
}

.custom-data-table {
  display: block;
  height: calc(50vh - 20px);
  overflow-y: scroll;
  overflow-x: hidden;
}

.checkbox-table-input {
  width: 20px !important;
}

.checkbox-table-input-header {
  margin: 0px 3px -5px 5px !important;
}

.checkbox-table-input-row {
  margin-left: 5px !important;
  margin-right: 3px !important;
}

#confirmation .bg-gray {
  background-color: #dbdbdb;
}
</style>



// WEBPACK FOOTER //
// New.vue?a000c6a8